import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

type ToastType = 'success' | 'error';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  leaving: boolean;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const AUTO_DISMISS_MS = 4000;
const LEAVE_MS = 200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), LEAVE_MS);
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'success') => {
      const id = nextId.current++;
      setToasts((ts) => [...ts, { id, message, type, leaving: false }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
      >
        {toasts.map((toast) => (
          <button
            key={toast.id}
            onClick={() => dismiss(toast.id)}
            className={clsx(
              'pointer-events-auto flex max-w-sm items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-white shadow-lg transition-all duration-200',
              toast.type === 'error' ? 'bg-red-600' : 'bg-flock-navy-800',
              toast.leaving ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
            )}
          >
            {toast.type === 'error' ? (
              <ExclamationCircleIcon className="h-5 w-5 shrink-0" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 shrink-0" />
            )}
            <span className="min-w-0">{toast.message}</span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
